/**
 * Remembers which preset view the work queue was last opened on, so someone who
 * lives in "To grade" does not land on "Today" every morning.
 *
 * Only the preset KEY is stored — never a search string, never a student or
 * staff id. A remembered search term would be the one piece of this screen's
 * state that can carry a person's name, and putting a name in localStorage buys
 * nothing: the rest of the filter set is already in the URL, which is where a
 * view someone wants to keep belongs.
 *
 * Same contract as `selected-batch-store.ts`, for the same reasons: scoped by
 * institute (one person can be staff at two), NOT scoped by user id (that would
 * invert the `shared/ → app/` layer direction), cleared on sign-out instead, and
 * every access wrapped in try/catch so a blocked localStorage is harmless. The
 * caller validates the string it gets back, so a stale or hand-edited key
 * degrades to the role default rather than throwing.
 */

const PREFIX = 'work_view:';

function storageKey(instituteId: string): string {
  return `${PREFIX}${instituteId}`;
}

export function readWorkView(instituteId: string): string | null {
  try {
    return localStorage.getItem(storageKey(instituteId));
  } catch {
    return null;
  }
}

export function writeWorkView(instituteId: string, view: string): void {
  try {
    localStorage.setItem(storageKey(instituteId), view);
  } catch {
    /* storage unavailable — the choice still holds for this session */
  }
}

/** Drop every remembered view, for every institute. Called on sign-out. */
export function clearWorkViews(): void {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key !== null && key.startsWith(PREFIX)) doomed.push(key);
    }
    doomed.forEach((key) => {
      localStorage.removeItem(key);
    });
  } catch {
    /* storage unavailable — nothing was persisted to begin with */
  }
}
